/**
 * Phase 18b — one leads list's own settings inside a container: an amount, a
 * gateway pin and three send options, for every drop the container sends to
 * that list. Precedence (user, 10 Oct 2026): a slot's own amount / gateway
 * pin → the list's → the container's. Options merge onto the container's
 * send defaults; an absent key keeps the default.
 * For Media Devoted | Created By Ciro Autiero
 */

export interface DropGenListOverrideOptions {
  /** A link domain value, or "auto". */
  shortLinkTldValue?: string;
  /** Include recipients already messaged today (SMSWiser sameDayRestriction off). */
  forceSend?: boolean;
  useOnlyNewDomains?: boolean;
}

export interface DropGenListOverride {
  wiserId: number;
  /** null = the slot's or the container's amount. */
  amount: number | null;
  /** null = the gateway strategy decides. */
  routeId: number | null;
  /** Phase 40 — the lane the pin picks (null = the route's first lane). */
  gatewayRowId?: string | null;
  options: DropGenListOverrideOptions | null;
}

export const DROP_GEN_LIST_OVERRIDE_LIMITS = { max: 500, amountMax: 1_000_000 } as const;

/** Stored JSON → the options it really sets (unknown keys and wrong types dropped). */
export function parseListOverrideOptions(json: unknown): DropGenListOverrideOptions | null {
  if (typeof json !== "object" || json === null) return null;
  const r = json as Record<string, unknown>;
  const out: DropGenListOverrideOptions = {};
  if (typeof r.shortLinkTldValue === "string" && r.shortLinkTldValue.trim()) out.shortLinkTldValue = r.shortLinkTldValue;
  if (typeof r.forceSend === "boolean") out.forceSend = r.forceSend;
  if (typeof r.useOnlyNewDomains === "boolean") out.useOnlyNewDomains = r.useOnlyNewDomains;
  return Object.keys(out).length > 0 ? out : null;
}

/** The plain-words summary the editor and the board print ("1 SMS · gateway #11 · TLD .info"). */
export function describeListOverride(o: Pick<DropGenListOverride, "amount" | "routeId" | "options">, routeName?: string | null): string {
  const parts: string[] = [];
  if (o.amount !== null) parts.push(`${o.amount.toLocaleString("en-US")} SMS`);
  if (o.routeId !== null) parts.push(`gateway ${routeName ?? `#${o.routeId}`}`);
  const opt = o.options ?? {};
  if (opt.shortLinkTldValue) parts.push(`link domain ${opt.shortLinkTldValue}`);
  if (opt.forceSend !== undefined) parts.push(opt.forceSend ? "duplicates on" : "duplicates off");
  if (opt.useOnlyNewDomains !== undefined) parts.push(opt.useOnlyNewDomains ? "only new domains" : "any domain");
  return parts.length > 0 ? parts.join(" · ") : "nothing overridden";
}
